import { LayoutKey } from '../../../types/manifest'
import { DeviceError, DeviceErrorCode } from '../../../errors/DeviceError'
import { isValidRowCol as sharedIsValidRowCol } from '../../shared/feature-impls/keyboard-lighting/lighting'
import {
  NestedLevelAddress,
  LevelAddress,
  CommonAddress,
  DeepNestedLevelAddress,
} from '../../../types/protocol'

// ==================== 按键矩阵常量 ====================
/** 按键矩阵列数 */
const COLUMN_COUNT = 22
/** 按键矩阵行数 */
const ROW_COUNT = 6

// ==================== 地址常量 ====================
// IQS93xx 校准参数储区域
const IQS93xx_CALIBRATION_ADDRESS = [0x10000000, 0x10001000, 0x1001e000, 0x1001f000]
// 配置存储区
// 配置参数存储区
const ADDRESS_LEVEL = [0x10002000, 0x10007000, 0x1000c000, 0x10011000]
const LAYER_ADDRESS_MAP = {
  // windows 基础层键映射存储区
  WINDOWS_BASE_LAYER_ADDRESS: [0x10003000, 0x10008000, 0x1000d000, 0x10012000],
  // windows Fn 层键映射存储区
  WINDOWS_FN_LAYER_ADDRESS: [0x10004000, 0x10009000, 0x1000e000, 0x10013000],
  // MAC 基础层键映射存储区
  MAC_BASE_LAYER_ADDRESS: [0x10005000, 0x1000a000, 0x1000f000, 0x10014000],
  // MAC Fn 层键映射存储区
  MAC_FN_LAYER_ADDRESS: [0x10006000, 0x1000b000, 0x10010000, 0x10015000],
}

// 用户自定义静态灯效模式参数存储区
const STATIC_LIGHT_EFFECT_PRESET_ADDRESS = 0x10016000
// 用户自定义静态灯效参数表
const STATIC_LIGHT_EFFECT_PRESET_ADDRESS_LEVEL = [
  0x0010, 0x0030, 0x0050, 0x0070, 0x0090, 0x00b0, 0x00d0, 0x00f0, 0x0110, 0x0130, 0x0150, 0x0170,
  0x0190, 0x01b0, 0x01d0,
]

// 用户自定义静态模式灯效 RGB 存储区
const STATIC_LIGHT_EFFECT_ADDRESS_LEVEL = [
  0x1001a000, 0x1001a400, 0x1001a800, 0x1001ac00, 0x1001b000, 0x1001b400, 0x1001b800, 0x1001bc00,
  0x1001c000, 0x1001c400, 0x1001c800, 0x1001cc00, 0x1001d000, 0x1001d400, 0x1001d800,
]

// 动态模式参数 详见 背光灯效动态模式参数表
const DYNAMIC_LIGHT_EFFECT_ADDRESS_LEVEL = [
  0x0034, 0x003c, 0x0044, 0x004c, 0x0054, 0x005c, 0x0064, 0x006c,
]

// 侧灯动态模式参数 详见 背光灯效静态模式参数表
const SIDE_DYNAMIC_LIGHT_EFFECT_ADDRESS_LEVEL = [0x00b4, 0x00bc, 0x00c4, 0x00cc]

// 宏读写区域
const MACRO_PACKAGE_ADDRESS_LEVEL = 0x10020000
// 宏名称
const MACRO_NAME_ADDRESS_LEVEL = 0x10030000

/**
 * 获取校准参数表
 * @returns LevelAddress
 */
export const genrateCalibrationAddress = (offset: number): LevelAddress => {
  const data = IQS93xx_CALIBRATION_ADDRESS.map((level) => {
    const real = level + offset
    return [
      real & 0x000000ff,
      (real & 0x0000ff00) >> 8,
      (real & 0x00ff0000) >> 16,
      (real & 0xff000000) >> 24,
    ] as CommonAddress
  })
  return data as LevelAddress
}

/**
 * 配置参数表
 * 根据偏移量计算 真实的LevelAddress
 * @param offset 偏移量
 * @returns LevelAddress
 */
export const genrateLevelAddress = (offset: number): LevelAddress => {
  const data = ADDRESS_LEVEL.map((level) => {
    const real = level + offset
    return [
      real & 0x000000ff,
      (real & 0x0000ff00) >> 8,
      (real & 0x00ff0000) >> 16,
      (real & 0xff000000) >> 24,
    ] as CommonAddress
  })
  return data as LevelAddress
}

/**
 * 生成键程及死区配置地址（4个配置层 × 6行 × 22列）
 * 单键占用8字节存储键程+死区参数，对应R0-C0~R5-C21的6×22按键矩阵
 * @param offset 全局基础偏移量（叠加到所有地址）
 * @returns DeepNestedLevelAddress 三维数组：[配置层][行][列] = CommonAddress
 */
export const genrateKeyTravelAndDeadZoneAddress = (offset: number): DeepNestedLevelAddress => {
  // 键程及死区定义(6x22 列,单键占用 8 字节)
  const KEY_BYTE_SIZE = 8 // 单键键程+死区参数占用8字节
  const COLUMN_STEP = KEY_BYTE_SIZE // 列偏移步长（每列+8字节）
  const ROW_STEP = COLUMN_COUNT * COLUMN_STEP // 行偏移步长（每行=22列×8字节=176字节）

  // 遍历4个配置层的基础地址（复用ADDRESS_LEVEL）
  const layerData = ADDRESS_LEVEL.map((layerBaseAddr) => {
    // 生成6行数据（R0~R5）
    const rowData: NestedLevelAddress = []
    for (let row = 0; row < ROW_COUNT; row++) {
      // 生成当前行的22列数据（C0~C21）
      const colData: LevelAddress = []
      const rowOffset = row * ROW_STEP // 当前行的基础偏移
      for (let col = 0; col < COLUMN_COUNT; col++) {
        // 计算当前按键的绝对地址：配置层基础地址 + 全局偏移 + 行偏移 + 列偏移
        const colOffset = col * COLUMN_STEP
        const realAddr = layerBaseAddr + offset + rowOffset + colOffset

        // 转换为CommonAddress格式（4字节拆分：低字节→高字节）
        const commonAddr: CommonAddress = [
          realAddr & 0x000000ff, // 第0字节（最低位）
          (realAddr & 0x0000ff00) >> 8, // 第1字节
          (realAddr & 0x00ff0000) >> 16, // 第2字节
          (realAddr & 0xff000000) >> 24, // 第3字节（最高位）
        ]
        colData.push(commonAddr)
      }
      rowData.push(colData)
    }
    return rowData
  })

  return layerData as DeepNestedLevelAddress
}
/**
 * 生成Windows基础层键映射地址（4个配置层 × 6行 × 22列）
 * 对应R0-C0~R5-C21的6×22按键矩阵
 * @param baseOffset 全局基础偏移量（如0x0000，可叠加到所有地址）
 * @returns LevelAddress 三维数组：[配置层][行][列] = CommonAddress
 */
export const genrateWindowsBaseLayerAddress = (
  baseOffset: number = 0,
  type: keyof typeof LAYER_ADDRESS_MAP = 'WINDOWS_BASE_LAYER_ADDRESS',
): DeepNestedLevelAddress => {
  // 每列偏移0x10，每行22列，行偏移步长=22*0x10=0x160
  const COLUMN_STEP = 0x10 // 每列地址偏移步长（+0x0000→+0x0010→...）
  const ROW_STEP = COLUMN_COUNT * COLUMN_STEP // 每行地址偏移步长（0x160）

  // 遍历4个配置层的基础地址
  const layerData = LAYER_ADDRESS_MAP[type].map((layerBaseAddr) => {
    // 生成6行数据（R0~R5）
    const rowData: NestedLevelAddress = []
    for (let row = 0; row < ROW_COUNT; row++) {
      // 生成当前行的22列数据（C0~C21）
      const colData: LevelAddress = []
      const rowOffset = row * ROW_STEP // 当前行的基础偏移
      for (let col = 0; col < COLUMN_COUNT; col++) {
        // 计算当前按键的绝对地址：基础层地址 + 全局偏移 + 行偏移 + 列偏移
        const colOffset = col * COLUMN_STEP
        const realAddr = layerBaseAddr + baseOffset + rowOffset + colOffset

        const commonAddr: CommonAddress = [
          realAddr & 0x000000ff,
          (realAddr & 0x0000ff00) >> 8,
          (realAddr & 0x00ff0000) >> 16,
          (realAddr & 0xff000000) >> 24,
        ]
        colData.push(commonAddr)
      }
      rowData.push(colData)
    }
    return rowData
  })

  return layerData as DeepNestedLevelAddress
}

/**
 * 将行列号转换为横向排列的 index（行优先：index = row * COLUMN_COUNT + col）
 * @param row 行号（0-based）
 * @param col 列号（0-based）
 * @returns index
 */
export const rowColToIndex = (row: number, col: number): number => {
  return row * COLUMN_COUNT + col
}

/**
 * 将横向排列的 index 转换为行列号
 * @param index 横向排列的索引（0-based）
 * @returns { row, col } 行号和列号（均为 0-based）
 */
export const indexToRowCol = (index: number): { row: number; col: number } => {
  return {
    row: Math.floor(index / COLUMN_COUNT),
    col: index % COLUMN_COUNT,
  }
}

/**
 * 指定行列有效性校验
 * @param row 行号（0-based）
 * @param col 列号（0-based）
 * @returns 是否有效
 */
export const isValidRowCol = (row: number, col: number, LayoutKeys: LayoutKey[][]): boolean => {
  return sharedIsValidRowCol(row, col, COLUMN_COUNT, ROW_COUNT, LayoutKeys)
}

// 自定义静态模式灯效的名称
export const genrateStaticLightEffectPresetAddressName = (offset: number): LevelAddress => {
  const data = STATIC_LIGHT_EFFECT_PRESET_ADDRESS_LEVEL.map((level) => {
    const real = STATIC_LIGHT_EFFECT_PRESET_ADDRESS + level + offset
    return [
      real & 0x000000ff,
      (real & 0x0000ff00) >> 8,
      (real & 0x00ff0000) >> 16,
      (real & 0xff000000) >> 24,
    ] as CommonAddress
  })
  return data as LevelAddress
}

// 自定义静态模式灯效的参数表（预设的静态灯效）
export const genrateStaticLightEffectPresetAddressLevel = (offset: number): LevelAddress => {
  const data = STATIC_LIGHT_EFFECT_PRESET_ADDRESS_LEVEL.map((level) => {
    // Byte 0x00 ~ Byte 0x0B: 灯效名称
    // Byte 0x0C ~ Byte 0x13: 灯效显示控制参数
    // Byte 0x14 ~ Byte 0x1f: 保留
    const real = STATIC_LIGHT_EFFECT_PRESET_ADDRESS + level + 0x0c + offset
    return [
      real & 0x000000ff,
      (real & 0x0000ff00) >> 8,
      (real & 0x00ff0000) >> 16,
      (real & 0xff000000) >> 24,
    ] as CommonAddress
  })
  return data as LevelAddress
}

// 用户自定义灯效表
export const genrateStaticLightEffectAddressLevel = (offset: number): LevelAddress => {
  const data = STATIC_LIGHT_EFFECT_ADDRESS_LEVEL.map((level) => {
    const real = level + offset
    return [
      real & 0x000000ff,
      (real & 0x0000ff00) >> 8,
      (real & 0x00ff0000) >> 16,
      (real & 0xff000000) >> 24,
    ] as CommonAddress
  })
  return data as LevelAddress
}

/**
 * 自定义静态灯效RGB地址（15个灯效存储区 × 6行 × 22列）
 * 每个点位返回「红色分量」地址（R0C0红/R0C1红/.../R5C21红）
 * 灯效表格式：[灯效存储区][行][列] = 对应点位红色分量的CommonAddress
 * @param offset 全局基础偏移量（叠加到所有地址）
 * @returns DeepNestedLevelAddress 三维数组：[灯效存储区][行][列] = CommonAddress
 */
export const genrateStaticLightRGBAddressLevel = (offset: number = 0): DeepNestedLevelAddress => {
  // 灯效表规则：
  // 每个点位占4字节：0=红,1=绿,2=蓝,3=保留 → 列步长=4字节
  // 6行（R0~R5），22列（C0~C21）
  const PIXEL_STEP = 4 // 每个点位（R/G/B/保留）占4字节 → 列偏移步长
  const ROW_STEP = COLUMN_COUNT * PIXEL_STEP // 每行总字节数 = 22列×4字节 = 88字节

  // 遍历8个灯效存储区的基础地址
  const lightEffectData = STATIC_LIGHT_EFFECT_ADDRESS_LEVEL.map((lightBaseAddr) => {
    // 生成6行数据（R0~R5）
    const rowData: NestedLevelAddress = []
    for (let row = 0; row < ROW_COUNT; row++) {
      // 生成当前行的22列数据（C0~C21）
      const colData: LevelAddress = []
      const rowOffset = row * ROW_STEP // 当前行的基础偏移（R0=0, R1=88, R2=176...）
      for (let col = 0; col < COLUMN_COUNT; col++) {
        // 计算当前点位「红色分量」的绝对地址：
        // 灯效存储区基础地址 + 全局偏移 + 行偏移 + 列偏移（红色分量是每个点位的第0字节）
        const colOffset = col * PIXEL_STEP
        const realAddr = lightBaseAddr + offset + rowOffset + colOffset

        const commonAddr: CommonAddress = [
          realAddr & 0x000000ff,
          (realAddr & 0x0000ff00) >> 8,
          (realAddr & 0x00ff0000) >> 16,
          (realAddr & 0xff000000) >> 24,
        ]
        colData.push(commonAddr)
      }
      rowData.push(colData)
    }
    return rowData
  })

  return lightEffectData as DeepNestedLevelAddress
}

// 动态模式参数，详见 背光灯效动态模式参数表
export const genrateDynamicLightEffectAddressLevel = (offset: number): NestedLevelAddress => {
  const data = ADDRESS_LEVEL.map((configBaseAddr) => {
    return DYNAMIC_LIGHT_EFFECT_ADDRESS_LEVEL.map((dynamicBaseAddr) => {
      const realAddr = configBaseAddr + dynamicBaseAddr + offset
      return [
        realAddr & 0x000000ff,
        (realAddr & 0x0000ff00) >> 8,
        (realAddr & 0x00ff0000) >> 16,
        (realAddr & 0xff000000) >> 24,
      ] as CommonAddress
    })
  })
  return data as NestedLevelAddress
}

// 侧灯动态模式参数，详见 背光灯效动态模式参数表
export const genrateSideLightEffectAddressLevel = (offset: number): NestedLevelAddress => {
  const data = ADDRESS_LEVEL.map((configBaseAddr) => {
    return SIDE_DYNAMIC_LIGHT_EFFECT_ADDRESS_LEVEL.map((dynamicBaseAddr) => {
      const realAddr = configBaseAddr + dynamicBaseAddr + offset
      return [
        realAddr & 0x000000ff,
        (realAddr & 0x0000ff00) >> 8,
        (realAddr & 0x00ff0000) >> 16,
        (realAddr & 0xff000000) >> 24,
      ] as CommonAddress
    })
  })
  return data as NestedLevelAddress
}

// 宏读写区域，512字节为一组宏
export const genrateMacroAddressLevel = (offset: number): LevelAddress => {
  const MACRO_COUNT = 128 // 总宏数量
  const MACRO_BYTE_SIZE = 512 // 每条宏最大字节数

  const data = Array.from({ length: MACRO_COUNT }, (_, index) => {
    const realAddr = MACRO_PACKAGE_ADDRESS_LEVEL + index * MACRO_BYTE_SIZE + offset
    return [
      realAddr & 0x000000ff,
      (realAddr & 0x0000ff00) >> 8,
      (realAddr & 0x00ff0000) >> 16,
      (realAddr & 0xff000000) >> 24,
    ] as CommonAddress
  })

  return data as LevelAddress
}

// 宏名称，每个占用 16 字节
export const genrateMacroNameAddressLevel = (offset: number): LevelAddress => {
  const MACRO_NAME_COUNT = 128 // 宏名称数量（对应128条宏）
  const MACRO_NAME_BYTE_SIZE = 16 // 每个名称占用字节数

  const data = Array.from({ length: MACRO_NAME_COUNT }, (_, index) => {
    const realAddr = MACRO_NAME_ADDRESS_LEVEL + index * MACRO_NAME_BYTE_SIZE + offset
    return [
      realAddr & 0x000000ff,
      (realAddr & 0x0000ff00) >> 8,
      (realAddr & 0x00ff0000) >> 16,
      (realAddr & 0xff000000) >> 24,
    ] as CommonAddress
  })

  return data as LevelAddress
}

// 宏单包地址
export const genrateMacroPackageAddressLevel = (
  offset: CommonAddress,
  macroTotalLength: number,
  maxPackageLength: number = 24,
  maxMacroCount: number = 512,
): LevelAddress => {
  if (macroTotalLength <= 0 || macroTotalLength > maxMacroCount) {
    throw new DeviceError(
      DeviceErrorCode.INVALID_PARAM,
      `The length of the macro (${macroTotalLength}) exceeds the allowed range (1 to ${maxMacroCount}).`,
    )
  }
  if (maxPackageLength <= 0) {
    throw new DeviceError(
      DeviceErrorCode.INVALID_PARAM,
      `The length of a single packet must be greater than 0.`,
    )
  }
  if (!Array.isArray(offset) || offset.length !== 4) {
    throw new DeviceError(
      DeviceErrorCode.INVALID_PARAM,
      `The offset must be a CommonAddress type (4-digit number array).`,
    )
  }

  const startAddr = (offset[3] << 24) | (offset[2] << 16) | (offset[1] << 8) | offset[0]

  const packageCount = Math.ceil(macroTotalLength / maxPackageLength)
  const packageAddresses: LevelAddress = []

  for (let i = 0; i < packageCount; i++) {
    const packageAddr = startAddr + i * maxPackageLength
    packageAddresses.push([
      packageAddr & 0x000000ff,
      (packageAddr & 0x0000ff00) >> 8,
      (packageAddr & 0x00ff0000) >> 16,
      (packageAddr & 0xff000000) >> 24,
    ] as CommonAddress)
  }

  return packageAddresses
}
